import { Controller, type SubmitHandler, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { itemSchema, type ItemFormInput, type ItemFormOutput } from '../schemas/item.schema';
import { Form, Input, Button, Label } from '@/shared/components';
import type { ItemRecord } from '../services/smr-admin';

interface ItemFormProps {
    initialValues?: Partial<ItemRecord>;
    onSubmit: SubmitHandler<ItemFormOutput>;
    isLoading?: boolean;
    submitLabel?: string;
}

export function ItemForm({ initialValues, onSubmit, isLoading = false, submitLabel = 'Save' }: ItemFormProps) {
    const { handleSubmit, control, formState: { isSubmitting } } = useForm<ItemFormInput, unknown, ItemFormOutput>({
        resolver: zodResolver(itemSchema),
        defaultValues: {
            name: initialValues?.name ?? '',
            code: initialValues?.code ?? '',
            description: initialValues?.description ?? '',
        },
    });

    return (
        <Form className="space-y-4 max-w-2xl" onSubmit={handleSubmit(onSubmit)}>
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                <div className="md:col-span-2">
                    <Label htmlFor="name">Name</Label>
                    <Controller
                        name="name"
                        control={control}
                        render={({ field, fieldState: { error } }) => (
                            <Input id="name" placeholder="Item name" value={field.value} onChange={field.onChange} onBlur={field.onBlur} error={!!error} hint={error?.message} />
                        )}
                    />
                </div>
                <div>
                    <Label htmlFor="code">Code <span className="text-mp-muted">(optional)</span></Label>
                    <Controller
                        name="code"
                        control={control}
                        render={({ field, fieldState: { error } }) => (
                            <Input id="code" placeholder="Item code" value={field.value ?? ''} onChange={field.onChange} onBlur={field.onBlur} error={!!error} hint={error?.message} />
                        )}
                    />
                </div>
                <div className="md:col-span-2">
                    <Label htmlFor="description">Description</Label>
                    <Controller
                        name="description"
                        control={control}
                        render={({ field, fieldState: { error } }) => (
                            <Input id="description" placeholder="Description" value={field.value ?? ''} onChange={field.onChange} onBlur={field.onBlur} error={!!error} hint={error?.message} />
                        )}
                    />
                </div>
            </div>
            <Button size="sm" disabled={isLoading || isSubmitting}>
                {isLoading || isSubmitting ? 'Saving...' : submitLabel}
            </Button>
        </Form>
    );
}
